import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { useWidgetErrorBanner } from '../../hooks/widget/useWidgetErrorBanner'
import { useWinRateWidget } from '../../hooks/widget/dashboard/useWinRateWidget'
import { useHoursPlayedWidget } from '../../hooks/widget/dashboard/useHoursPlayedWidget'
import { useNextSessionWidget } from '../../hooks/widget/dashboard/useNextSessionWidget'
import { useSavedPartnersWidget } from '../../hooks/widget/dashboard/useSavedPartnersWidget'
import { useMonthlyActivityWidget } from '../../hooks/widget/dashboard/useMonthlyActivityWidget'
import { useUpcomingBookingsWidget } from '../../hooks/widget/dashboard/useUpcomingBookingsWidget'
import { useRemainingMinutesWidget } from '../../hooks/widget/dashboard/useRemainingMinutesWidget'
import { useRefundPointsWidget } from '../../hooks/widget/dashboard/useRefundPointsWidget'

import KPI from '../../components/ui/kpi/KPI'
import { Widget } from '../../components/widget/Widget'
import { WidgetGrid } from '../../components/widget/WidgetGrid'
import { NextSession } from '../../components/authenticated/dashboard/nextSession/NextSession'
import { UpcomingList } from '../../components/authenticated/dashboard/upcomingBooking/UpcomingList'
import { SavedPartners } from '../../components/authenticated/dashboard/savedPartners/SavedParteners'
import { GateAccessWidget } from '../../components/authenticated/dashboard/gameAccess/GateAccessWidget'
import { LockerAccessWidget } from '../../components/authenticated/dashboard/lockerAccess/LockerAccessWidget'
import { RemainingMinutes } from '../../components/authenticated/dashboard/remainingSessions/RemainingMinutes'
import { MonthlyActivityCard } from '../../components/authenticated/dashboard/monthlyActivityCard/MonthlyActivityCard'

import { WidgetTone } from '../../types/widget/widget'
import { FeatureFlags, isFeatureEnabled } from '../../constants/featureFlags'
import { formatPoints } from '../../utils/booking/booking'
import { toDisplayLocalDate } from '../../utils/dateUtils'

export const Dashboard = () => {
  const { t, i18n } = useTranslation('dashboard')
  const nextSessionResult = useNextSessionWidget()
  const monthlyActivityResult = useMonthlyActivityWidget()
  const remainingMinutesResult = useRemainingMinutesWidget()
  const hoursPlayedResult = useHoursPlayedWidget()
  const winRateResult = useWinRateWidget()
  const upcomingBookingsResult = useUpcomingBookingsWidget()
  const savedPartnersResult = useSavedPartnersWidget()
  const refundPointsResult = useRefundPointsWidget()
  const handleWidgetError = useWidgetErrorBanner()
  const gateWidgetsEnabled = isFeatureEnabled(FeatureFlags.FF_GATE)
  const lockerWidgetsEnabled = isFeatureEnabled(FeatureFlags.FF_LOCKER)
  const partnersWidgetsEnabled = isFeatureEnabled(FeatureFlags.FF_PARTNERS)
  const membershipsWidgetsEnabled = isFeatureEnabled(
    FeatureFlags.FF_MEMBERSHIPS
  )
  const showRemainingMinutes =
    membershipsWidgetsEnabled && remainingMinutesResult.data !== null
  const mergeRemainingMinutes =
    showRemainingMinutes && monthlyActivityResult.data !== null
  const showSavedPartners =
    partnersWidgetsEnabled && savedPartnersResult.data !== null

  return (
    <>
      <WidgetGrid>
        <Widget
          result={nextSessionResult}
          onError={handleWidgetError}
          className="col-12"
          renderWhenEmpty
          bare
        >
          {data => <NextSession nextSession={data} />}
        </Widget>
        {gateWidgetsEnabled && nextSessionResult.data?.bookingStartTime ? (
          <GateAccessWidget
            bookingStartTime={nextSessionResult.data.bookingStartTime}
          />
        ) : null}
        {lockerWidgetsEnabled && nextSessionResult.data?.bookingStartTime ? (
          <LockerAccessWidget
            bookingStartTime={nextSessionResult.data.bookingStartTime}
          />
        ) : null}
        <Widget
          result={monthlyActivityResult}
          onError={handleWidgetError}
          className={showRemainingMinutes ? 'col-12 col-lg-8' : 'col-12'}
          tone={WidgetTone.DARK}
        >
          {data => (
            <MonthlyActivityCard
              playedSessions={data}
              minutes={
                mergeRemainingMinutes ? remainingMinutesResult.data : null
              }
            />
          )}
        </Widget>
        {showRemainingMinutes ? (
          <Widget
            result={remainingMinutesResult}
            onError={handleWidgetError}
            className={`col-12 col-lg-4 ${mergeRemainingMinutes ? 'd-none d-lg-flex' : ''}`}
            tone={WidgetTone.DARK}
          >
            {data => <RemainingMinutes minutes={data} />}
          </Widget>
        ) : null}
        <Widget
          result={hoursPlayedResult}
          onError={handleWidgetError}
          className="col-6 col-lg-4 dashboard-kpi"
          tone={WidgetTone.ACCENT}
        >
          {data => (
            <KPI
              label={t('dashboard.banner.kpi.hours-played')}
              value={data?.value ?? '—'}
              delta={data?.changePercentage ?? undefined}
              sub={t('dashboard.banner.kpi.this-month')}
              deltaFallback={
                data ? t('dashboard.banner.kpi.no-previous-data') : undefined
              }
              tone="accent"
            />
          )}
        </Widget>
        <Widget
          result={winRateResult}
          onError={handleWidgetError}
          className="col-6 col-lg-4 dashboard-kpi"
        >
          {data => (
            <KPI
              label={t('dashboard.banner.kpi.win-rate')}
              value={data?.value ?? '—'}
              delta={data?.changePercentage ?? undefined}
              sub={t('dashboard.banner.kpi.this-month')}
              deltaFallback={
                data ? t('dashboard.banner.kpi.no-previous-data') : undefined
              }
            />
          )}
        </Widget>
        <Widget
          result={refundPointsResult}
          onError={handleWidgetError}
          className="col-12 col-lg-4 dashboard-kpi"
          renderWhenEmpty
        >
          {data => (
            <Link to="/account/refunds" className="text-reset text-decoration-none d-block h-100">
              <KPI
                label={t('dashboard.banner.kpi.refund-points')}
                value={formatPoints(data?.available)}
                sub={
                  data?.nextExpiresAt
                    ? t('dashboard.banner.kpi.refund-points-expiry', {
                        points: formatPoints(data.nextExpiryAmount),
                        date: toDisplayLocalDate(data.nextExpiresAt, i18n.language),
                      })
                    : t('dashboard.banner.kpi.refund-points-sub')
                }
              />
            </Link>
          )}
        </Widget>
        <Widget
          result={upcomingBookingsResult}
          onError={handleWidgetError}
          className={showSavedPartners ? 'col-12 col-lg-7' : 'col-12'}
        >
          {data => <UpcomingList bookingList={data} />}
        </Widget>
        {showSavedPartners ? (
          <Widget
            result={savedPartnersResult}
            onError={handleWidgetError}
            className="col-12 col-lg-5"
          >
            {data => <SavedPartners partners={data} />}
          </Widget>
        ) : null}
      </WidgetGrid>
    </>
  )
}

export default Dashboard
